import { env } from '@/config/env';
import {
  getFirebaseIdToken,
  signOutUser,
} from '@/services/auth/authService';
import type { ApiResponse } from '@/types/api';

export type ApiError = {
  status: number;
  message: string;
  code?: string;
  workerError?: string;
  details?: unknown;
};

export class ApiClientError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly workerError?: string;
  readonly details?: unknown;

  constructor(payload: ApiError) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.status = payload.status;
    if (payload.code !== undefined) this.code = payload.code;
    if (payload.workerError !== undefined) this.workerError = payload.workerError;
    if (payload.details !== undefined) this.details = payload.details;
  }
}

export type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Firebase ID Token para rutas protegidas del Worker. */
  authToken?: string;
};

export type AuthenticatedApiRequestOptions = Omit<
  ApiRequestOptions,
  'authToken'
> & {
  /** Por defecto usa `getFirebaseIdToken()` del servicio de auth. */
  getToken?: () => Promise<string | null>;
  /** Si true, renueva el token antes del request. */
  forceRefreshToken?: boolean;
};

/**
 * Cliente HTTP hacia erp-worker (Cloudflare).
 * Respuestas envueltas en `ApiResponse<T>` del Worker.
 */
export function getApiBaseUrl(): string {
  return env.apiBaseUrl;
}

export function isApiConfigured(): boolean {
  return getApiBaseUrl().length > 0;
}

function buildUrl(path: string): string {
  const base = getApiBaseUrl();
  if (!base) {
    throw new ApiClientError({
      status: 0,
      message:
        'API Worker no configurada. Definí VITE_API_BASE_URL (ver .env.example).',
      code: 'api_not_configured',
    });
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base.replace(/\/$/, '')}${normalizedPath}`;
}

function mapHttpStatusToApiError(
  status: number,
  workerError: string | undefined,
  text: string,
  statusText: string,
): ApiError {
  if (status === 401) {
    return {
      status: 401,
      message:
        workerError ??
        'Sesión inválida o expirada. Volvé a iniciar sesión.',
      code: 'session_invalid',
    };
  }
  if (status === 403) {
    return {
      status: 403,
      message:
        workerError ??
        'No tenés permisos para realizar esta acción.',
      code: 'forbidden',
    };
  }
  return {
    status,
    message: workerError ?? text ?? statusText,
    code: 'http_error',
  };
}

function isApiResponseBody(value: unknown): value is ApiResponse<unknown> {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return typeof o.success === 'boolean' && typeof o.timestamp === 'string';
}

type ParseEnvelopeOptions = {
  /** Cierra sesión Firebase ante 401 (solo requests autenticados al Worker). */
  signOutOnSessionInvalid?: boolean;
};

async function parseWorkerEnvelope<T>(
  res: Response,
  parseOptions: ParseEnvelopeOptions = {},
): Promise<T> {
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      throw new ApiClientError({
        status: res.status,
        message: text || res.statusText || 'Respuesta no JSON del Worker',
        code: 'invalid_json',
      });
    }
  }

  if (!res.ok) {
    const workerError =
      isApiResponseBody(parsed) && typeof parsed.error === 'string'
        ? parsed.error
        : undefined;
    const httpErr: ApiError = mapHttpStatusToApiError(
      res.status,
      workerError,
      text,
      res.statusText,
    );
    if (workerError !== undefined) httpErr.workerError = workerError;
    if (
      isApiResponseBody(parsed) &&
      parsed.details !== undefined
    ) {
      httpErr.details = parsed.details;
    }
    if (
      parseOptions.signOutOnSessionInvalid &&
      res.status === 401
    ) {
      void signOutUser().catch(() => undefined);
    }
    throw new ApiClientError(httpErr);
  }

  if (!isApiResponseBody(parsed)) {
    if (parsed === undefined && res.status === 204) {
      return undefined as T;
    }
    throw new ApiClientError({
      status: res.status,
      message: 'Formato de respuesta inválido (se esperaba ApiResponse)',
      code: 'invalid_envelope',
    });
  }

  if (!parsed.success) {
    const workerErr: ApiError = {
      status: res.status,
      message: parsed.error ?? 'Error del Worker',
      code: 'worker_error',
    };
    if (parsed.error !== undefined) workerErr.workerError = parsed.error;
    throw new ApiClientError(workerErr);
  }

  return parsed.data as T;
}

/**
 * Request HTTP sin envolver auth. Usar `apiPublicRequest` / `apiAuthenticatedRequest`.
 */
export async function apiRawRequest(
  path: string,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.authToken) {
    headers.Authorization = `Bearer ${options.authToken}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  return fetch(buildUrl(path), init);
}

/** GET/POST/etc. a rutas públicas del Worker (`/health`, `/version`). */
export async function apiPublicRequest<T>(
  path: string,
  options: Omit<ApiRequestOptions, 'authToken'> = {},
): Promise<T> {
  const res = await apiRawRequest(path, options);
  return parseWorkerEnvelope<T>(res);
}

/** Request con Firebase ID Token; el Worker verifica JWT (JWKS) y rol en Firestore. */
export async function apiAuthenticatedRequest<T>(
  path: string,
  options: AuthenticatedApiRequestOptions = {},
): Promise<T> {
  const { getToken, forceRefreshToken, ...rest } = options;
  const resolveToken =
    getToken ??
    (() => getFirebaseIdToken(forceRefreshToken ?? false));

  const authToken = await resolveToken();
  if (!authToken) {
    throw new ApiClientError({
      status: 401,
      message: 'Sesión requerida. Iniciá sesión para llamar al Worker.',
      code: 'auth_required',
    });
  }

  const res = await apiRawRequest(path, { ...rest, authToken });
  return parseWorkerEnvelope<T>(res, { signOutOnSessionInvalid: true });
}

/**
 * @deprecated Preferí `apiPublicRequest` o `apiAuthenticatedRequest`.
 * Mantiene compatibilidad con llamadas que esperan el cuerpo crudo o tipado directo.
 */
export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const res = await apiRawRequest(path, options);
  return parseWorkerEnvelope<T>(res);
}

export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiClientError) {
    const out: ApiError = { status: err.status, message: err.message };
    if (err.code !== undefined) out.code = err.code;
    if (err.workerError !== undefined) out.workerError = err.workerError;
    if (err.details !== undefined) out.details = err.details;
    return out;
  }
  if (err && typeof err === 'object' && 'status' in err && 'message' in err) {
    return err as ApiError;
  }
  return {
    status: 0,
    message: err instanceof Error ? err.message : 'Error desconocido',
  };
}
